import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import {
  CreditCard,
  Add,
  Close,
  CheckCircle,
  AccountBalance,
  Assignment,
  Info
} from '@mui/icons-material';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import FlutterServicesAPI from '../../../services/flutterServices';
import { creditsAPI } from '../../../services/merchantAPI';

const MerchantWelcomeSection = ({ merchant: propMerchant, navigate, showWelcomeText = true }) => {
  const [customCreditsDialog, setCustomCreditsDialog] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);
  const [isUpdatingCredits, setIsUpdatingCredits] = useState(false);
  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();

  const { merchant: contextMerchant, refreshProfile } = useMerchantAuth();
  const canPurchaseCredits = hasPermission('Credits Management', 'purchase_credits');


  // Use context merchant data if available, otherwise fall back to prop
  const merchant = contextMerchant || propMerchant;

  // Refresh profile on component mount to ensure latest credit balance
  useEffect(() => {
    if (contextMerchant) {
      refreshProfile();
    }
  }, []);

  const handleCustomCredits = async () => {
    if (!customAmount || customAmount < 100) {
      setError(`Minimum custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} amount is 100`);
      return;
    }

    try {
      setLoading(true);
      setIsUpdatingCredits(true);
      const response = await creditsAPI.requestCustomCredits({
        amount: parseInt(customAmount),
        notes: customNotes
      });

      setCustomCreditsDialog(false);
      setCustomAmount('');
      setCustomNotes('');

      // Show beautiful success dialog
      setSuccessMessage(`Custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request submitted successfully!`);
      setRequestDetails({
        requestId: response.data?.request_id,
        status: response.data?.status || 'PENDING',
        instructions: 'Your request will be reviewed by our team and you will be contacted within 24-48 hours.',
        credits: customAmount,
        packageName: `Custom ${FlutterServicesAPI.getCreditLabel()}`
      });
      setSuccessDialog(true);

      // Refresh profile to get updated credit balance
      await refreshProfile();

    } catch (error) {
      console.error('Custom credit request failed:', error);
      setError(`Failed to submit custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request. Please try again.`);
    } finally {
      setLoading(false);
      setIsUpdatingCredits(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog(false);
    setSuccessMessage('');
    setRequestDetails(null);
  };

  const handleCancelCustomCredits = () => {
    setCustomCreditsDialog(false);
    setCustomAmount('');
    setCustomNotes('');
    setError('');
  };

  return (
    <Box sx={{ pt: 2 }}>
      {/* Mobile/Tablet Layout */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {/* Welcome and transform lines - only show if showWelcomeText is true and not on mobile */}
        {showWelcomeText && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="h4" fontWeight={900} gutterBottom
              sx={{
                background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                fontSize: { xs: '1.5rem', sm: '2.125rem' },
                lineHeight: 1.2,
                mb: 1
              }}
            >
              Welcome to Glammify
            </Typography>

            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '12px', sm: '14px' } }}>
              Transform your products with AI-powered visualization
            </Typography>
          </Box>
        )}

        {/* Credit section - always show */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
            borderRadius: 3,
            p: 2,
            boxShadow: 2,
            color: 'white',
            gap: 2,
            mb: showWelcomeText ? 0 : 3, // Add margin bottom if no welcome text
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 2,
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditCard sx={{ color: 'white', fontSize: 28 }} />
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ opacity: 0.85, fontWeight: 500, fontSize: { xs: 10, sm: 12 } }}>
              Available {FlutterServicesAPI.getCreditLabel()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: { xs: 24, sm: 28 } }}>
                {merchant?.credits_balance !== undefined ? merchant.credits_balance : 0}
              </Typography>
              {isUpdatingCredits && (
                <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.8)' }} />
              )}
            </Box>
          </Box>
          {!permissionLoading && canPurchaseCredits && !FlutterServicesAPI.isFlutterApp() && (
            <Button
              variant="contained"
              onClick={() => setCustomCreditsDialog(true)}
              sx={{
                background: 'white',
                color: 'black',
                fontWeight: 500,
                fontSize: { xs: 12, sm: 14 },
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': {
                  background: 'white',
                  color: 'black',
                  boxShadow: 'none',
                },
              }}
            >
              <Add sx={{ fontSize: 18, mr: 1 }} />
              Request {FlutterServicesAPI.getCreditLabel()}
            </Button>
          )}
        </Box>
      </Box>

      {/* Desktop Layout - always show full welcome section */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900} gutterBottom
            sx={{
              background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Welcome to Glammify
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Transform your products with AI-powered visualization
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
            borderRadius: 3,
            p: 1.5,
            minWidth: 260,
            boxShadow: 2,
            color: 'white',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <Box
              sx={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 2,
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5,
              }}
            >
              <CreditCard sx={{ color: 'white', fontSize: 32 }} />
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ opacity: 0.85, fontWeight: 500, fontSize: { xs: 12, sm: 12, md: 16 } }}>
              Available {FlutterServicesAPI.getCreditLabel()}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: 32 }}>
                {merchant?.credits_balance !== undefined ? merchant.credits_balance : 0}
              </Typography>
              {isUpdatingCredits && (
                <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.8)' }} />
              )}
            </Box>
          </Box>
          {!permissionLoading && canPurchaseCredits && !FlutterServicesAPI.isFlutterApp() && (
            <Button
              variant="contained"
              onClick={() => setCustomCreditsDialog(true)}
              sx={{
                ml: 2,
                background: 'white',
                color: 'black',
                fontWeight: 500,
                fontSize: 14,
                borderRadius: 2,
                px: 3,
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': {
                  background: 'white',
                  color: 'black',
                  boxShadow: 'none',
                },
              }}
            >
              <Add sx={{ fontSize: 18, mr: 1 }} />
              Request {FlutterServicesAPI.getCreditLabel()}
            </Button>
          )}
        </Box>
      </Box>

      {/* Custom Credits Dialog */}
      <Dialog open={customCreditsDialog} onClose={handleCancelCustomCredits} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Request Custom {FlutterServicesAPI.getCreditLabel()}
            <IconButton onClick={handleCancelCustomCredits} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Request a custom {FlutterServicesAPI.getCreditLabel().toLowerCase()} amount for your business needs. Minimum 100 {FlutterServicesAPI.getCreditLabel().toLowerCase()}.
          </Typography>
          <TextField
            fullWidth
            label={`${FlutterServicesAPI.getCreditLabel()} Required *`}
            type="number"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            placeholder={`Enter number of ${FlutterServicesAPI.getCreditLabel().toLowerCase()} needed (minimum 100)`}
            inputProps={{ min: 100 }}
            helperText={`Minimum 100 ${FlutterServicesAPI.getCreditLabel().toLowerCase()} required`}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Reason/Notes (Optional)"
            multiline
            rows={3}
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            placeholder={`Please provide a reason for your custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request`}
            helperText={`Explain why you need custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} (business purpose, project details, etc.)`}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelCustomCredits}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomCredits}
            variant="contained"
            disabled={loading || !customAmount || parseInt(customAmount) < 100}
            sx={{
              background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #7c3aed 0%, #1d4ed8 100%)',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beautiful Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
            color: 'white',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 1 }}>
          <IconButton
            onClick={handleCloseSuccessDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              '&:hover': { background: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <Box
              sx={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Success!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, textAlign: 'center' }}>
              {successMessage}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            {requestDetails && (
              <Card sx={{ mt: 2, background: 'rgba(255,255,255,0.1)', color: 'white' }}>
                <CardContent sx={{ py: 2 }}>
                  <Grid container spacing={2}>
                    {requestDetails.packageName && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <CreditCard sx={{ fontSize: 20 }} />
                          <Typography variant="body1" fontWeight="bold">
                            {requestDetails.packageName}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {requestDetails.credits && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <AccountBalance sx={{ fontSize: 20 }} />
                          <Typography variant="body1">
                            {requestDetails.credits} {FlutterServicesAPI.getCreditLabel()}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {requestDetails.requestId && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1, background: 'rgba(255,255,255,0.3)' }} />
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <Assignment sx={{ fontSize: 20 }} />
                          <Typography variant="body2">
                            Request ID: {requestDetails.requestId}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {requestDetails.status && (
                      <Grid item xs={12}>
                        <Chip
                          label={`Status: ${requestDetails.status}`}
                          sx={{
                            background: requestDetails.status === 'PENDING' ? '#FF9800' : '#4CAF50',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </Grid>
                    )}

                    {requestDetails.instructions && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2, p: 2, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            <Info sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                            {requestDetails.instructions}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Typography variant="body2" sx={{ mt: 3, opacity: 0.8 }}>
              You will be notified once your request is processed.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            size="large"
            sx={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white !important',
              fontWeight: 'bold',
              px: 4,
              '&:hover': {
                background: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MerchantWelcomeSection; 